import { IsoDateString, TimeString, UsDateString, isoToUs, usToIso } from './date';

/**
 * A single generated emergency shift block. Shape matches the
 * `customShiftProps` rows `handleSaveShift` already builds, so blocks can be
 * pushed straight into its pipeline. `endTime < startTime` marks an overnight
 * block (ends the following day); `date`/`blockDate` are the block's own start
 * calendar day ("MM-DD-YYYY").
 */
export interface EmergencyBlock {
    startTime: TimeString;
    endTime: TimeString;
    date: UsDateString;
    blockDate: UsDateString;
}

// The preferred "7-to-7" grid: full blocks run 07:00 -> 19:00 (day) and
// 19:00 -> 07:00 (night). Boundaries as minutes-of-day.
const MORNING_BOUNDARY = 7 * 60; // 07:00
const EVENING_BOUNDARY = 19 * 60; // 19:00
const MINUTES_PER_DAY = 24 * 60;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/;

/**
 * Absolute minutes since the Unix epoch for a date + time. Uses `Date.UTC`
 * (no DST, no minute-offset) and pure string parsing so the result never
 * depends on the browser's local timezone.
 */
export const toInstant = (iso: IsoDateString, time: TimeString): number | null => {
    if (!ISO_DATE_PATTERN.test(iso)) {
        return null;
    }
    const timeMatch = TIME_PATTERN.exec(time);
    if (!timeMatch) {
        return null;
    }
    const parts = iso.split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    if ([year, month, day, hours, minutes].some(Number.isNaN)) {
        return null;
    }
    const dayIndex = Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
    return dayIndex * MINUTES_PER_DAY + hours * 60 + minutes;
};

/** "MM-DD-YYYY" for the calendar day an absolute-minute instant falls on. */
export const usDateForInstant = (instant: number): UsDateString => {
    const dayIndex = Math.floor(instant / MINUTES_PER_DAY);
    const date = new Date(dayIndex * 86_400_000);
    const iso = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    return isoToUs(iso);
};

/** "HH:mm:ss" for an absolute-minute instant (time-of-day portion). */
export const timeForInstant = (instant: number): TimeString => {
    const minuteOfDay = ((instant % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const hours = Math.floor(minuteOfDay / 60);
    const minutes = minuteOfDay % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
};

/**
 * The day an emergency window ends on. Cover that runs past midnight ends the
 * morning after the last day the admin picked, so a single overnight shift
 * needs one date on the calendar rather than two. An end time later in the day
 * than the start ends on the last picked day itself.
 *
 * Equal times mean a full day: 07:00 to 07:00 on one date runs to the next
 * morning.
 *
 * @param  lastDateIso The latest date picked, YYYY-MM-DD
 * @param  startTime   When the cover starts on the first picked date
 * @param  endTime     When the cover ends
 * @return the end date, YYYY-MM-DD
 */
export const emergencyWindowEnd = (
    lastDateIso: IsoDateString,
    startTime: TimeString,
    endTime: TimeString,
): IsoDateString => {
    const start = toInstant(lastDateIso, startTime);
    const end = toInstant(lastDateIso, endTime);

    if (start === null || end === null || end > start) {
        return lastDateIso;
    }

    return usToIso(usDateForInstant(end + MINUTES_PER_DAY));
};

/**
 * Split an arbitrary [start, end] window into shift blocks aligned to the
 * 7-to-7 grid (07:00 / 19:00), with the leading/trailing "odd hours" as their
 * own shorter blocks. Ordered start -> end, spanning multiple days; the final
 * block ends exactly at the window end. Returns [] on bad/empty input.
 *
 * Example — 2025-07-29 21:00 -> 2025-07-31 09:00:
 *   29th 21:00-07:00 (overnight) | 30th 07:00-19:00 | 30th 19:00-07:00
 *   (overnight) | 31st 07:00-09:00 (trailing remainder).
 */
export const splitEmergencyWindow = (
    startIso: IsoDateString,
    startTime: TimeString,
    endIso: IsoDateString,
    endTime: TimeString,
): EmergencyBlock[] => {
    const startInstant = toInstant(startIso, startTime);
    const endInstant = toInstant(endIso, endTime);

    if (startInstant === null || endInstant === null || endInstant <= startInstant) {
        return [];
    }

    const blocks: EmergencyBlock[] = [];
    let cursor = startInstant;

    const maxIterations = Math.ceil((endInstant - startInstant) / MINUTES_PER_DAY) * 2 + 4;
    let iterations = 0;

    while (cursor < endInstant && iterations < maxIterations) {
        iterations += 1;

        const dayStart = Math.floor(cursor / MINUTES_PER_DAY) * MINUTES_PER_DAY;
        const minuteOfDay = cursor - dayStart;

        let nextBoundary: number;
        if (minuteOfDay < MORNING_BOUNDARY) {
            nextBoundary = dayStart + MORNING_BOUNDARY;
        } else if (minuteOfDay < EVENING_BOUNDARY) {
            nextBoundary = dayStart + EVENING_BOUNDARY;
        } else {
            nextBoundary = dayStart + MINUTES_PER_DAY + MORNING_BOUNDARY;
        }

        const blockEnd = Math.min(nextBoundary, endInstant);
        const usDate = usDateForInstant(cursor);

        blocks.push({
            startTime: timeForInstant(cursor),
            endTime: timeForInstant(blockEnd),
            date: usDate,
            blockDate: usDate,
        });

        cursor = blockEnd;
    }

    return blocks;
};
